import { useCommand } from "./useCommand"
import type { CommandKey, ExecutionResult } from "../types"
import type {
  CommandSource,
  UseCommandOptions,
  CommandInvoker,
} from "./useCommand"

// ============================================================================
// TYPES
// ============================================================================

export interface UseInvokerOptions<TInput = any, TOutput = any> {
  source?: CommandSource
  throwOnError?: boolean
  defaultInput?: Partial<TInput>
  onSuccess?: (result: TOutput) => void
  onError?: (error: Error) => void
}

/**
 * Hook simplificado para execução de comandos
 *
 * Retorna diretamente a função de execução, sem necessidade de chamar .invoke()
 *
 * @param key - Chave do comando a ser executado
 * @param options - Opções simplificadas de configuração
 * @returns Função de execução direta
 */
export function useInvoker<TInput = any, TOutput = any>(
  key: CommandKey,
  options: UseInvokerOptions<TInput, TOutput> = {},
): (input?: TInput) => Promise<TOutput> {
  const {
    source = "api",
    throwOnError = true,
    defaultInput,
    onSuccess,
    onError,
  } = options

  // Converte opções do useInvoker para useCommand
  const commandOptions: UseCommandOptions<TInput, TOutput> = {
    source,
    throwOnError,
    defaultInput,
    onSuccess,
    onError,
  }

  const commandInvoker = useCommand(key, commandOptions)

  // Retorna apenas a função invoke
  return commandInvoker.invoke
}

// ============================================================================
// SPECIALIZED HOOKS
// ============================================================================

/**
 * Hook para comandos que não precisam de input
 */
export function useAction(
  key: CommandKey,
  options: Omit<UseInvokerOptions<void, any>, "defaultInput"> = {},
): () => Promise<any> {
  const invoke = useInvoker<void, any>(key, options)
  return () => invoke()
}

/**
 * Hook que retorna sempre o objeto CommandInvoker (alias para useCommand)
 */
export function useCommandState<TInput = any, TOutput = any>(
  key: CommandKey,
  options: UseInvokerOptions<TInput, TOutput> = {},
): CommandInvoker<TInput, TOutput> {
  // Converte opções simplificadas para useCommand
  const commandOptions: UseCommandOptions<TInput, TOutput> = {
    source: options.source,
    throwOnError: options.throwOnError,
    defaultInput: options.defaultInput,
    onSuccess: options.onSuccess,
    onError: options.onError,
  }

  return useCommand(key, commandOptions)
}

/**
 * Hook para execução segura (nunca lança erro)
 */
export function useSafeInvoker<TInput = any, TOutput = any>(
  key: CommandKey,
  options: Omit<UseInvokerOptions<TInput, TOutput>, "throwOnError"> = {},
): (input?: TInput) => Promise<ExecutionResult<TOutput>> {
  const invoker = useCommand(key, {
    ...options,
    throwOnError: false,
  })

  return (input?: TInput) => invoker.attempt(input)
}

/**
 * Hook para comandos com input pré-configurado
 */
export function useBoundInvoker<TInput = any, TOutput = any>(
  key: CommandKey,
  boundInput: Partial<TInput>,
  options: Omit<UseInvokerOptions<TInput, TOutput>, "defaultInput"> = {},
): (input?: Partial<TInput>) => Promise<TOutput> {
  const invoke = useInvoker<TInput, TOutput>(key, {
    ...options,
    defaultInput: boundInput,
  })

  return (input?: Partial<TInput>) => {
    const mergedInput = { ...boundInput, ...input } as TInput
    return invoke(mergedInput)
  }
}

/**
 * Hook para comandos toggle (boolean)
 */
export function useToggleInvoker(
  key: CommandKey,
  options: Omit<UseInvokerOptions<boolean, boolean>, "defaultInput"> = {},
): (state?: boolean) => Promise<boolean> {
  const invoke = useInvoker<boolean, boolean>(key, options)
  return (state?: boolean) => invoke(state)
}

// ============================================================================
// BATCH EXECUTION HOOKS
// ============================================================================

/**
 * Hook para execução sequencial de múltiplos comandos
 */
export function useBatchInvoker(
  keys: CommandKey[],
  options: {
    source?: CommandSource
    stopOnError?: boolean
    onProgress?: (completed: number, total: number) => void
  } = {},
): (
  inputs?: any[],
  batchOptions?: { stopOnError?: boolean },
) => Promise<ExecutionResult[]> {
  const invokers = keys.map((key) =>
    useCommand(key, {
      source: options.source,
      throwOnError: false,
    }),
  )

  return async (
    inputs: any[] = [],
    batchOptions: { stopOnError?: boolean } = {},
  ) => {
    const results: ExecutionResult[] = []
    const stopOnError = batchOptions.stopOnError ?? options.stopOnError ?? false

    for (let i = 0; i < keys.length; i++) {
      const input = inputs[i]
      const result = await invokers[i].attempt(input)

      results.push(result)

      if (options.onProgress) {
        options.onProgress(i + 1, keys.length)
      }

      if (stopOnError && !result.success) {
        break
      }
    }

    return results
  }
}

/**
 * Hook para execução paralela de múltiplos comandos
 */
export function useParallelInvoker(
  keys: CommandKey[],
  options: { source?: CommandSource } = {},
): (inputs?: any[]) => Promise<PromiseSettledResult<any>[]> {
  const invokers = keys.map((key) =>
    useCommand(key, {
      source: options.source,
    }),
  )

  return async (inputs: any[] = []) => {
    const promises = keys.map((_, i) => {
      const input = inputs[i]
      return invokers[i].invoke(input)
    })

    return Promise.allSettled(promises)
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useInvoker

// Re-export types from useCommand
export type {
  CommandInvoker,
  CommandSource,
  UseCommandOptions,
} from "./useCommand"
